import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Shield, Plus, Copy, Trash2, ToggleLeft, ToggleRight, Users, RefreshCw, Download, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VIPAdmin() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, uses: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', label: '', notes: '', max_uses: 1 });
  const [bulkConfig, setBulkConfig] = useState({ count: 10, prefix: 'VIP' });
  const [createdBulkCodes, setCreatedBulkCodes] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/vip-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(res.data.codes || []);
      setStats({ total: res.data.total_codes, uses: res.data.total_uses, active: res.data.active_codes });
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
      toast.error('Accès refusé');
    } finally {
      setLoading(false);
    }
  };

  const createCode = async () => {
    if (!newCode.code || !newCode.label) { toast.error('Code et label requis'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/vip-codes`, {
        code: newCode.code.toUpperCase(),
        label: newCode.label,
        tier: 'platinum',
        max_uses: parseInt(newCode.max_uses) || 1,
        notes: newCode.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Code ${newCode.code.toUpperCase()} créé !`);
      setNewCode({ code: '', label: '', notes: '', max_uses: 1 });
      setShowCreate(false);
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const createBulk = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/admin/vip-codes/bulk?count=${bulkConfig.count}&prefix=${bulkConfig.prefix}&tier=platinum&max_uses=1`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreatedBulkCodes(res.data.codes || []);
      toast.success(`${res.data.codes?.length} codes créés !`);
      fetchCodes();
    } catch (err) {
      toast.error('Erreur création bulk');
    } finally { setCreating(false); }
  };

  const toggleCode = async (code, active) => {
    await axios.patch(`${API}/admin/vip-codes/${code}/toggle`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success(`Code ${active ? 'désactivé' : 'activé'}`);
    fetchCodes();
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    await axios.delete(`${API}/admin/vip-codes/${code}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Code supprimé');
    fetchCodes();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copié !`);
  };

  const exportCodes = () => {
    const available = codes.filter(c => c.active && c.uses_count < (c.max_uses || 999));
    const csv = ['Code,Label,Uses,Max Uses,Status,Notes'].concat(
      available.map(c => `${c.code},"${c.label}",${c.uses_count},${c.max_uses || '∞'},${c.active ? 'Active' : 'Inactive'},"${c.notes || ''}"`)
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'donotbull-vip-codes.csv';
    a.click();
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="vip-admin" />
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-[#ff6b35]" />
              <span className="text-[#ff6b35] text-xs font-bold uppercase tracking-widest">Admin Panel</span>
            </div>
            <h1 className="text-3xl font-black text-white">Codes VIP</h1>
            <p className="text-gray-400 text-sm">Gérez les accès lifetime pour vos VIPs, amis et partenaires</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={exportCodes} variant="outline" className="border-gray-700 text-gray-400" size="sm">
              <Download className="w-4 h-4 mr-2" /> Exporter CSV
            </Button>
            <Button onClick={() => setShowBulk(!showBulk)} variant="outline" className="border-[#ff6b35]/30 text-[#ff6b35]" size="sm">
              <Zap className="w-4 h-4 mr-2" /> Créer en masse
            </Button>
            <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#ff6b35] text-black font-bold" size="sm">
              <Plus className="w-4 h-4 mr-2" /> Nouveau code
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total codes', value: stats.total, color: 'text-white' },
            { label: 'Codes actifs', value: stats.active, color: 'text-green-400' },
            { label: 'Utilisations', value: stats.uses, color: 'text-[#ff6b35]' },
          ].map((s, i) => (
            <div key={i} className="glass rounded-2xl p-5 text-center">
              <div className={`text-3xl font-black ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Create single code */}
        {showCreate && (
          <div className="glass rounded-2xl p-6 mb-6 border border-[#ff6b35]/20">
            <h3 className="font-bold mb-4">Créer un code personnalisé</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Code (ex: VIP-DAVID)</label>
                <Input
                  value={newCode.code}
                  onChange={e => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  placeholder="VIP-DAVID"
                  className="bg-black border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Label (pour vous)</label>
                <Input
                  value={newCode.label}
                  onChange={e => setNewCode({ ...newCode, label: e.target.value })}
                  placeholder="David - Ami proche"
                  className="bg-black border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Utilisations max</label>
                <Input
                  type="number"
                  value={newCode.max_uses}
                  onChange={e => setNewCode({ ...newCode, max_uses: e.target.value })}
                  className="bg-black border-gray-700 text-white"
                  min="1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Notes (optionnel)</label>
                <Input
                  value={newCode.notes}
                  onChange={e => setNewCode({ ...newCode, notes: e.target.value })}
                  placeholder="Influenceur Instagram @david"
                  className="bg-black border-gray-700 text-white"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={createCode} disabled={creating} className="bg-[#ff6b35] text-black font-bold">
                {creating ? 'Création...' : 'Créer le code'}
              </Button>
              <Button onClick={() => setShowCreate(false)} variant="outline" className="border-gray-700 text-gray-400">
                Annuler
              </Button>
            </div>
          </div>
        )}

        {/* Bulk create */}
        {showBulk && (
          <div className="glass rounded-2xl p-6 mb-6 border border-purple-500/20">
            <h3 className="font-bold mb-4">Créer des codes en masse</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Préfixe</label>
                <Input
                  value={bulkConfig.prefix}
                  onChange={e => setBulkConfig({ ...bulkConfig, prefix: e.target.value.toUpperCase() })}
                  placeholder="VIP"
                  className="bg-black border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Nombre de codes</label>
                <Input
                  type="number"
                  value={bulkConfig.count}
                  onChange={e => setBulkConfig({ ...bulkConfig, count: parseInt(e.target.value) })}
                  className="bg-black border-gray-700 text-white"
                  min="1" max="200"
                />
              </div>
            </div>
            <Button onClick={createBulk} disabled={creating} className="bg-purple-500 text-white font-bold mr-3">
              {creating ? 'Création...' : `Créer ${bulkConfig.count} codes`}
            </Button>
            <Button onClick={() => setShowBulk(false)} variant="outline" className="border-gray-700 text-gray-400">
              Annuler
            </Button>

            {/* Show created codes */}
            {createdBulkCodes.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-green-400 font-semibold">{createdBulkCodes.length} codes créés !</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-400"
                    onClick={() => {
                      navigator.clipboard.writeText(createdBulkCodes.join('\n'));
                      toast.success('Tous les codes copiés !');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-1" /> Copier tout
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {createdBulkCodes.map(code => (
                    <button
                      key={code}
                      onClick={() => copyCode(code)}
                      className="text-xs bg-gray-900 border border-gray-700 rounded-lg p-2 text-[#ff6b35] font-mono hover:border-[#ff6b35]/50 transition-colors"
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Codes table */}
        <div className="glass rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-gray-800 flex items-center justify-between">
            <h2 className="font-bold flex items-center gap-2">
              <Users className="w-4 h-4 text-[#ff6b35]" />
              Tous les codes ({codes.length})
            </h2>
            <Button onClick={fetchCodes} variant="ghost" size="sm" className="text-gray-400">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {codes.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400">Aucun code VIP créé</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-[#ff6b35] text-black font-bold" size="sm">
                Créer le premier code
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-left">
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Code</th>
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Label</th>
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Utilisations</th>
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Utilisés par</th>
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Statut</th>
                    <th className="p-4 text-xs text-gray-500 font-semibold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code, i) => (
                    <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-[#ff6b35] text-sm">{code.code}</span>
                          <button onClick={() => copyCode(code.code)} className="text-gray-600 hover:text-[#ff6b35]">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-white">{code.label}</div>
                        {code.notes && <div className="text-xs text-gray-500">{code.notes}</div>}
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <span className={code.uses_count > 0 ? 'text-green-400 font-bold' : 'text-gray-400'}>
                            {code.uses_count}
                          </span>
                          <span className="text-gray-600"> / {code.max_uses || '∞'}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {code.used_by?.length > 0 ? (
                          <div className="space-y-1">
                            {code.used_by.map((u, j) => (
                              <div key={j} className="text-xs text-gray-400">
                                {u.name} <span className="text-gray-600">({u.email})</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-600">Pas encore utilisé</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${code.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {code.active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleCode(code.code, code.active)}
                            className="text-gray-500 hover:text-[#ff6b35] transition-colors"
                            title={code.active ? 'Désactiver' : 'Activer'}
                          >
                            {code.active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => deleteCode(code.code)}
                            className="text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="glass rounded-2xl p-6 mt-6 border border-[#ff6b35]/10">
          <h3 className="font-bold text-[#ff6b35] mb-3">Comment ça marche ?</h3>
          <div className="space-y-2 text-sm text-gray-400">
            <p>1. Créez un code VIP (ex: <span className="font-mono text-[#ff6b35]">VIP-DAVID</span>)</p>
            <p>2. Envoyez-le à votre contact : <em>"Inscris-toi sur donotbull.com et entre le code VIP-DAVID pour un accès lifetime gratuit"</em></p>
            <p>3. Lors de l'inscription, il entre le code → accès Premium automatique et permanent</p>
            <p>4. Vous voyez ici qui a utilisé quel code</p>
          </div>
        </div>
      </div>
    </div>
  );
}
